import React, { useEffect, useMemo, useState } from 'react';
import { Save, CheckCircle2, Trash2, FolderOpen, Play } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFechaDeMarca } from '../utils/format';
import AutocompleteProducto from '../components/AutocompleteProducto';
import BotonAyuda from '../components/BotonAyuda';

const money = (n) => (n === null || n === undefined ? '—' : `$${formatMonto(n)}`);

function DeltaBadge({ delta, deltaPct }) {
  if (delta === null || delta === undefined) return <span className="text-slate-400">—</span>;
  const up = delta > 0.0001;
  const down = delta < -0.0001;
  const cls = up ? 'bg-amber-100 text-amber-700' : down ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500';
  const signo = up ? '+' : '';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${cls}`}>
      {signo}${formatMonto(delta)}{deltaPct !== null && deltaPct !== undefined ? ` · ${signo}${formatMonto(deltaPct)}%` : ''}
    </span>
  );
}

export default function SimulacionCosto({ embebido = false } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [modo, setModo] = useState('uno');
  const [productos, setProductos] = useState([]);
  const [tasa, setTasa] = useState('BCV');
  const [escenarios, setEscenarios] = useState([]);
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  // modo "uno"
  const [productoUno, setProductoUno] = useState(null);
  const [estructura, setEstructura] = useState(null);
  const [overrides, setOverrides] = useState({}); // componente_id -> { valor, moneda }
  const [mermaUno, setMermaUno] = useState('');
  const [resUno, setResUno] = useState(null);

  // modo "tabla"
  const [seleccion, setSeleccion] = useState(new Set());
  const [filtro, setFiltro] = useState('');
  const [componenteBulk, setComponenteBulk] = useState(null);
  const [bulkValor, setBulkValor] = useState('');
  const [bulkMoneda, setBulkMoneda] = useState('USD');
  const [bulkAjuste, setBulkAjuste] = useState('');
  const [resTabla, setResTabla] = useState([]);

  async function cargar() {
    const [prod, esc] = await Promise.all([api.get('/productos'), api.get('/costo-escenario')]);
    setProductos(prod.data);
    setEscenarios(esc.data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  const productosFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return productos.filter((p) => !q || p.nombre.toLowerCase().includes(q));
  }, [productos, filtro]);

  async function elegirProductoUno(id) {
    setProductoUno(id);
    setResUno(null);
    setEstructura(null);
    setOverrides({});
    if (!id) return;
    try {
      const { data } = await api.get('/producto-receta/costo-vivo', { params: { producto_id: id, tasa } });
      setEstructura(data);
      setMermaUno(data.merma_pct ?? 0);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar la estructura');
    }
  }

  function construirCambiosUno() {
    if (!estructura) return [];
    const cambios = [];
    for (const g of estructura.grupos || []) {
      for (const l of g.lineas || []) {
        const key = l.es_costo_compra ? 'self' : l.componente_id;
        const ov = overrides[key];
        if (ov && ov.valor !== '' && ov.valor !== undefined && Number(ov.valor) >= 0) {
          cambios.push({
            producto_id: productoUno,
            objetivo: 'costo_componente',
            componente_id: l.es_costo_compra ? productoUno : l.componente_id,
            valor_nuevo: Number(ov.valor),
            moneda: ov.moneda || 'USD'
          });
        }
      }
    }
    if (mermaUno !== '' && Number(mermaUno) !== Number(estructura.merma_pct)) {
      cambios.push({ producto_id: productoUno, objetivo: 'merma', valor_nuevo: Number(mermaUno) });
    }
    return cambios;
  }

  async function simularUno() {
    setError('');
    const cambios = construirCambiosUno();
    if (cambios.length === 0) { setError('No hay cambios para simular'); return; }
    try {
      const { data } = await api.post('/costo-escenario/simular', { productos: [productoUno], tasa, cambios });
      setResUno(data.filas[0] || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo simular');
    }
  }

  function cambiosTabla() {
    const ids = [...seleccion];
    if (!componenteBulk || ids.length === 0) return [];
    const usarAjuste = bulkAjuste !== '' && (bulkValor === '' || bulkValor === undefined);
    return ids.map((pid) => ({
      producto_id: pid,
      objetivo: 'costo_componente',
      componente_id: componenteBulk,
      valor_nuevo: usarAjuste ? null : (bulkValor !== '' ? Number(bulkValor) : null),
      ajuste_pct: usarAjuste ? Number(bulkAjuste) : null,
      moneda: bulkMoneda || 'USD'
    }));
  }

  async function simularTabla() {
    setError('');
    const cambios = cambiosTabla();
    if (cambios.length === 0) { setError('Elegí productos y un insumo/actividad a cambiar'); return; }
    try {
      const { data } = await api.post('/costo-escenario/simular', { productos: [...seleccion], tasa, cambios });
      setResTabla(data.filas);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo simular');
    }
  }

  async function guardarEscenario() {
    setError(''); setMsg('');
    if (!nombre.trim()) { setError('Ponele un nombre al escenario'); return null; }
    const cambios = modo === 'uno' ? construirCambiosUno() : cambiosTabla();
    if (cambios.length === 0) { setError('No hay cambios para guardar'); return null; }
    try {
      const { data } = await api.post('/costo-escenario', { nombre: nombre.trim(), cambios });
      await cargar();
      setMsg('Escenario guardado');
      return data.id;
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
      return null;
    }
  }

  async function guardarYAplicar() {
    const id = await guardarEscenario();
    if (!id) return;
    if (!window.confirm('Esto crea registros reales en el historial de costo con fecha de hoy y ajusta la merma. ¿Aplicar?')) return;
    try {
      const { data } = await api.post(`/costo-escenario/${id}/aplicar`);
      const r = data.resumen;
      setMsg(`Aplicado: ${r.costos_creados} costo(s), ${r.mermas_actualizadas} merma(s)${r.omitidos.length ? `, ${r.omitidos.length} omitido(s)` : ''}`);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo aplicar');
    }
  }

  async function aplicarEscenario(id) {
    if (!window.confirm('Aplicar este escenario a los costos reales (fecha de hoy)?')) return;
    try {
      const { data } = await api.post(`/costo-escenario/${id}/aplicar`);
      const r = data.resumen;
      setMsg(`Aplicado: ${r.costos_creados} costo(s), ${r.mermas_actualizadas} merma(s)`);
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo aplicar');
    }
  }

  async function borrarEscenario(id) {
    if (!window.confirm('¿Borrar este escenario?')) return;
    try {
      await api.delete(`/costo-escenario/${id}`);
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  }

  async function abrirEscenario(esc) {
    setError(''); setMsg(''); setResUno(null); setResTabla([]);
    try {
      const { data } = await api.get(`/costo-escenario/${esc.id}`);
      const cambios = data.cambios || [];
      setNombre(data.nombre);
      const productoIds = [...new Set(cambios.map((c) => c.producto_id))];
      if (productoIds.length > 1) {
        setModo('tabla');
        setSeleccion(new Set(productoIds));
        const cc = cambios.find((c) => c.objetivo === 'costo_componente');
        setComponenteBulk(cc ? cc.componente_id : null);
        setBulkValor(cc && cc.valor_nuevo != null ? cc.valor_nuevo : '');
        setBulkAjuste(cc && cc.ajuste_pct != null ? cc.ajuste_pct : '');
        setBulkMoneda(cc && cc.moneda ? cc.moneda : 'USD');
      } else {
        setModo('uno');
        const pid = productoIds[0];
        await elegirProductoUno(pid);
        const ov = {};
        for (const c of cambios) {
          if (c.objetivo === 'costo_componente' && c.componente_id != null) {
            const key = Number(c.componente_id) === Number(c.producto_id) ? 'self' : c.componente_id;
            ov[key] = { valor: c.valor_nuevo != null ? c.valor_nuevo : '', moneda: c.moneda || 'USD' };
          }
          if (c.objetivo === 'merma') setMermaUno(c.valor_nuevo);
        }
        setOverrides(ov);
      }
    } catch (err) {
      setError('No se pudo abrir el escenario');
    }
  }

  function toggleSel(id) {
    setSeleccion((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {embebido ? <span /> : (
          <h1 className="text-2xl font-bold text-slate-800">
            Simulación de costo
            {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
            <BotonAyuda />
          </h1>
        )}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Tasa</label>
          <select value={tasa} onChange={(e) => setTasa(e.target.value)} className="border rounded-md px-2 py-1 text-sm">
            <option value="BCV">BCV</option>
            <option value="USD">USD</option>
          </select>
        </div>
      </div>

      <p className="text-sm text-slate-500 -mt-3">
        Probá cambios de costo sin tocar los datos reales. Guardá el escenario con nombre y, si querés, aplicalo
        (crea registros en el historial de costo con fecha de hoy). La rentabilidad y la lista de precios se ven en la pestaña “Precios”.
      </p>

      <div className="inline-flex rounded-lg border bg-slate-50 p-1 text-sm">
        {['uno', 'tabla'].map((m) => (
          <button
            key={m}
            onClick={() => setModo(m)}
            className={`px-4 py-1.5 rounded-md font-medium ${modo === m ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500'}`}
          >
            {m === 'uno' ? 'Un producto' : 'Tabla de productos'}
          </button>
        ))}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {msg && <p className="text-emerald-700 text-sm">{msg}</p>}

      {modo === 'uno' && (
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <div className="w-72">
            <label className="block text-xs text-slate-500 mb-1">Producto a simular</label>
            <AutocompleteProducto
              productos={productos}
              value={productoUno}
              onChange={elegirProductoUno}
              placeholder="Buscar producto..."
            />
          </div>

          {estructura && (estructura.grupos || []).length === 0 && (
            <p className="text-sm text-slate-500">Este producto no tiene estructura de costo — cargala primero en Productos.</p>
          )}

          {estructura && (estructura.grupos || []).length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="text-slate-500 text-left">
                    <tr>
                      <th className="py-1 pr-2">Componente</th>
                      <th className="py-1 px-2 text-right">Cantidad</th>
                      <th className="py-1 px-2 text-right">Costo actual</th>
                      <th className="py-1 px-2 text-right">Costo simulado</th>
                      <th className="py-1 px-2">Moneda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(estructura.grupos || []).map((g) => (
                      <React.Fragment key={g.grupo_id ?? 'sin'}>
                        <tr className="bg-slate-50">
                          <td colSpan={5} className="py-1 px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{g.nombre}</td>
                        </tr>
                        {(g.lineas || []).map((l) => {
                          const key = l.es_costo_compra ? 'self' : l.componente_id;
                          const ov = overrides[key] || { valor: '', moneda: 'USD' };
                          return (
                            <tr key={key} className="border-t border-slate-100">
                              <td className="py-1.5 pr-2">{l.componente_nombre}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">{l.cantidad} {l.componente_unidad}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">
                                {l.costo_unitario_componente != null ? `$${formatMonto(l.costo_unitario_componente)}` : 'sin costo'}
                              </td>
                              <td className="py-1.5 px-2 text-right">
                                <input
                                  type="number"
                                  step="0.0001"
                                  value={ov.valor}
                                  placeholder={l.costo_unitario_componente != null ? formatMonto(l.costo_unitario_componente) : '0'}
                                  onChange={(e) => setOverrides({ ...overrides, [key]: { ...ov, valor: e.target.value } })}
                                  className="border rounded-md px-2 py-1 text-sm w-24 text-right"
                                />
                              </td>
                              <td className="py-1.5 px-2">
                                <select
                                  value={ov.moneda}
                                  onChange={(e) => setOverrides({ ...overrides, [key]: { ...ov, moneda: e.target.value } })}
                                  className="border rounded-md px-1 py-1 text-xs"
                                >
                                  <option value="USD">USD</option>
                                  <option value="BS">BS</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs uppercase tracking-wide text-slate-400 font-medium">Merma %</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={mermaUno}
                  onChange={(e) => setMermaUno(e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm w-20 text-right"
                />
                <span className="text-xs text-slate-400">actual {formatMonto(estructura.merma_pct)} %</span>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button onClick={simularUno} className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-md text-sm">
                  <Play size={15} /> Simular
                </button>
                {resUno && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-slate-500">{money(resUno.costo_actual)}</span>
                    <span className="text-slate-400">→</span>
                    <span className="text-lg font-semibold text-slate-800 tabular-nums">{money(resUno.costo_simulado)}</span>
                    <DeltaBadge delta={resUno.delta} deltaPct={resUno.delta_pct} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {modo === 'tabla' && (
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <label className="block text-xs text-slate-500 mb-1">Cambiar el costo de</label>
              <AutocompleteProducto
                productos={productos}
                value={componenteBulk}
                onChange={setComponenteBulk}
                placeholder="Insumo o actividad..."
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nuevo valor</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  step="0.0001"
                  value={bulkValor}
                  onChange={(e) => { setBulkValor(e.target.value); if (e.target.value) setBulkAjuste(''); }}
                  className="border rounded-md px-2 py-1.5 text-sm w-24 text-right"
                />
                <select value={bulkMoneda} onChange={(e) => setBulkMoneda(e.target.value)} className="border rounded-md px-1 py-1.5 text-xs">
                  <option value="USD">USD</option>
                  <option value="BS">BS</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">o ajuste %</label>
              <input
                type="number"
                step="0.5"
                value={bulkAjuste}
                onChange={(e) => { setBulkAjuste(e.target.value); if (e.target.value) setBulkValor(''); }}
                placeholder="+10"
                className="border rounded-md px-2 py-1.5 text-sm w-20 text-right"
              />
            </div>
            <button onClick={simularTabla} className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-md text-sm">
              <Play size={15} /> Simular seleccionados
            </button>
          </div>

          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar productos..."
            className="w-full sm:w-72 border rounded-md px-3 py-2 text-sm"
          />

          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="text-slate-500 text-left bg-slate-50">
                <tr>
                  <th className="py-2 px-3 w-10"></th>
                  <th className="py-2 px-2">Producto</th>
                  <th className="py-2 px-2 text-right">Costo actual</th>
                  <th className="py-2 px-2 text-right">Costo simulado</th>
                  <th className="py-2 px-2 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map((p) => {
                  const r = resTabla.find((f) => f.producto_id === p.id);
                  const sel = seleccion.has(p.id);
                  return (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="py-1.5 px-3">
                        <input type="checkbox" checked={sel} onChange={() => toggleSel(p.id)} />
                      </td>
                      <td className="py-1.5 px-2">{p.nombre}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-slate-500">{r ? money(r.costo_actual) : '—'}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-medium">{r && sel ? money(r.costo_simulado) : '—'}</td>
                      <td className="py-1.5 px-2 text-right">{r && sel ? <DeltaBadge delta={r.delta} deltaPct={r.delta_pct} /> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400">{seleccion.size} producto(s) seleccionado(s)</p>
        </div>
      )}

      {/* guardar / aplicar */}
      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-slate-500 mb-1">Nombre del escenario</label>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="ej. Queso +15% — proveedor nuevo"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />
        </div>
        <button onClick={guardarEscenario} title="Guardar escenario" className="bg-primary-600 hover:bg-primary-700 text-white p-2.5 rounded-md">
          <Save size={18} />
        </button>
        <button onClick={guardarYAplicar} title="Guardar y aplicar a costos reales" className="bg-emerald-600 hover:bg-emerald-700 text-white p-2.5 rounded-md">
          <CheckCircle2 size={18} />
        </button>
      </div>

      {/* escenarios guardados */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400 mb-2">Escenarios guardados</h2>
        <div className="bg-white border rounded-lg divide-y">
          {escenarios.length === 0 && <p className="p-4 text-sm text-slate-400">Todavía no hay escenarios.</p>}
          {escenarios.map((e) => (
            <div key={e.id} className="flex items-center gap-3 p-3 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-700 truncate">{e.nombre}</p>
                <p className="text-xs text-slate-400">
                  {e.n_productos} producto(s) · {e.n_cambios} cambio(s) · {formatFechaDeMarca(e.creado_en)}
                  {e.aplicado_en && <span className="text-emerald-600"> · aplicado {formatFechaDeMarca(e.aplicado_en)}</span>}
                </p>
              </div>
              <button onClick={() => abrirEscenario(e)} title="Abrir" className="text-slate-400 hover:text-primary-600">
                <FolderOpen size={16} />
              </button>
              <button onClick={() => aplicarEscenario(e.id)} title="Aplicar a costos reales" className="text-slate-400 hover:text-emerald-600">
                <CheckCircle2 size={16} />
              </button>
              <button onClick={() => borrarEscenario(e.id)} title="Borrar" className="text-slate-400 hover:text-red-600">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
